import type { CartLineItem, Product, ProductVariant } from '~/types'
import {
  cartLineToGtmItem,
  productToGtmItem,
  pushGtmEcommerceEvent,
  savePendingPurchase,
  type GtmPurchasePayload,
} from '~/utils/gtmEcommerce'
import { isGoogleTagManagerInitialized } from '~/utils/googleTagManager'

export function useGtmEcommerce() {
  const { googleTagManager } = useSiteSettings()
  const { analytics } = useCookieConsent()

  function canTrack(): boolean {
    return Boolean(
      import.meta.client
      && googleTagManager.value.enabled
      && googleTagManager.value.containerIds.length > 0
      && analytics.value
      && isGoogleTagManagerInitialized(),
    )
  }

  function trackViewItem(product: Product, variant?: ProductVariant) {
    if (!canTrack()) return
    const item = productToGtmItem(product, 1, variant)
    pushGtmEcommerceEvent('view_item', {
      currency: 'RON',
      value: item.price,
      items: [item],
    })
  }

  function trackAddToCart(product: Product, quantity = 1, variant?: ProductVariant) {
    if (!canTrack()) return
    const item = productToGtmItem(product, quantity, variant)
    pushGtmEcommerceEvent('add_to_cart', {
      currency: 'RON',
      value: item.price * item.quantity,
      items: [item],
    })
  }

  function trackBeginCheckout(items: CartLineItem[], value: number) {
    if (!canTrack() || !items.length) return
    pushGtmEcommerceEvent('begin_checkout', {
      currency: 'RON',
      value,
      items: items.map(cartLineToGtmItem),
    })
  }

  function storePendingPurchase(payload: GtmPurchasePayload) {
    savePendingPurchase(payload)
  }

  function trackPurchase(payload: GtmPurchasePayload): boolean {
    if (!canTrack()) return false

    pushGtmEcommerceEvent('purchase', {
      transaction_id: payload.transaction_id,
      currency: 'RON',
      value: payload.value,
      shipping: payload.shipping ?? 0,
      tax: payload.tax ?? 0,
      coupon: payload.coupon || undefined,
      items: payload.items,
    })
    return true
  }

  return {
    canTrack,
    trackViewItem,
    trackAddToCart,
    trackBeginCheckout,
    storePendingPurchase,
    trackPurchase,
  }
}
